
import { useState } from 'react';
import { Acquirer, ConnectedAccount } from './types';
import { formatRut } from '@/lib/rut';

interface EditConnectionFormProps {
  account: ConnectedAccount;
  acquirer: Acquirer;
  isSaving: boolean;
  onSave: (fields: Record<string, string>) => void;
  onCancel: () => void;
}

export default function EditConnectionForm({
  account,
  acquirer,
  isSaving,
  onSave,
  onCancel,
}: EditConnectionFormProps) {
  const [fieldValues, setFieldValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const field of acquirer.fields) {
      initial[field.name] = (account as any)[field.name] ?? '';
    }
    return initial;
  });

  const handleFieldChange = (name: string, value: string) => {
    setFieldValues((prev) => ({
      ...prev,
      [name]: name === 'rut' ? formatRut(value) : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(fieldValues);
  };

  return (
    <div className="mt-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="material-symbols-outlined text-primary text-base">edit</span>
        <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
          Editar credenciales — {account.acquirerName}
        </h4>
      </div>

      <form onSubmit={handleSubmit} autoComplete="off">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {acquirer.fields.map((field) => (
            <div key={field.name}>
              <label
                htmlFor={`edit-${field.name}`}
                className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
              >
                {field.label}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-slate-400 text-lg">
                    {field.icon}
                  </span>
                </div>
                <input
                  id={`edit-${field.name}`}
                  name={field.name}
                  type={field.type}
                  value={fieldValues[field.name] ?? ''}
                  onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  autoComplete={field.type === 'password' ? 'new-password' : 'off'}
                  className="block w-full pl-10 pr-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                />
              </div>
              {field.hint && (
                <p className="mt-1.5 text-xs text-slate-500">{field.hint}</p>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-primary hover:bg-blue-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <span className="material-symbols-outlined text-base animate-spin">
                  progress_activity
                </span>
                Guardando...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-base">save</span>
                Guardar
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
